/**
 * VÉRAULT Portal — Content Studio · Journal
 *
 * Enhanced Journal Management view inside Content Studio.
 *
 * Improvements over the legacy /portal/advisor/journal:
 *   · Live search across title/subtitle/category/destination
 *   · Filter tabs respect ?filter=<status> deep links from Dashboard
 *   · Otherwise visually matches the existing Journal list semantics
 *     (uses the same /api/cms/journal endpoint).
 */
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ExternalLink, Edit3, Trash2, BookOpen, Search, X, Share2, Star } from "lucide-react";
import {
  Surface,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
} from "../../../index";
import { RepurposeModal } from "./RepurposeModal";

const FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "draft", label: "Drafts", match: (e) => e.status === "draft" },
  { id: "scheduled", label: "Scheduled", match: (e) => e.status === "scheduled" },
  { id: "published", label: "Published", match: (e) => e.status === "published" },
];

export default function ContentJournal() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = (searchParams.get("filter") || "all").toLowerCase();
  const [entries, setEntries] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState(
    FILTERS.find((f) => f.id === initialFilter) ? initialFilter : "all",
  );
  const [query, setQuery] = React.useState("");
  const [campaignFilter, setCampaignFilter] = React.useState("");
  const [featuredOnly, setFeaturedOnly] = React.useState(false);
  const [campaigns, setCampaigns] = React.useState([]);
  const [repurposeEntry, setRepurposeEntry] = React.useState(null);
  const [repurposeToast, setRepurposeToast] = React.useState(null);

  const refresh = React.useCallback(() => {
    setEntries(null);
    api
      .get("/cms/journal")
      .then(({ data }) => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setError("Couldn't load the journal."));
  }, []);

  React.useEffect(() => {
    refresh();
    api
      .get("/cms/campaigns")
      .then(({ data }) => setCampaigns(Array.isArray(data) ? data : []))
      .catch(() => setCampaigns([]));
  }, [refresh]);

  React.useEffect(() => {
    // keep ?filter in sync with state (but only when not 'all')
    const next = new URLSearchParams(searchParams);
    if (filter === "all") next.delete("filter");
    else next.set("filter", filter);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const filtered = React.useMemo(() => {
    if (!entries) return null;
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    const q = query.trim().toLowerCase();
    let base = entries.filter(f.match);
    if (campaignFilter) {
      base = base.filter((e) => (e.campaign_ids || []).includes(campaignFilter));
    }
    if (featuredOnly) {
      base = base.filter((e) => !!e.featured);
    }
    if (!q) return base;
    return base.filter((e) =>
      [e.title, e.subtitle, e.category, e.destination_tag]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [entries, filter, query, campaignFilter, featuredOnly]);

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.title || "Untitled"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/cms/journal/${entry.id}`);
      setEntries((prev) => (prev || []).filter((e) => e.id !== entry.id));
    } catch {
      window.alert("Couldn't delete that entry.");
    }
  };

  return (
    <div className="space-y-7" data-testid="content-journal">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <nav className="flex flex-wrap gap-1" data-testid="content-journal-filters">
          {FILTERS.map((f) => {
            const count = entries ? entries.filter(f.match).length : null;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`content-journal-filter-${f.id}`}
                className={`relative px-3 py-1.5 font-body text-[13px] tracking-tight transition-colors duration-200 inline-flex items-center gap-1.5 ${
                  filter === f.id
                    ? "text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                <span className={filter === f.id ? "font-serif" : ""}>{f.label}</span>
                {count !== null && count > 0 && (
                  <span
                    className={`font-mono text-[10px] tabular-nums ${
                      filter === f.id ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]/70"
                    }`}
                  >
                    {count}
                  </span>
                )}
                <span
                  className={`absolute -bottom-px left-3 right-3 h-px ${
                    filter === f.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[hsl(var(--foreground-muted))]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search entries…"
              data-testid="content-journal-search"
              className="h-9 pl-8 pr-7 w-56 text-[13px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)] rounded-full focus:outline-none focus:border-[hsl(var(--accent-gold))] transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              >
                <X className="size-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
          {campaigns.length > 0 && (
            <select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              data-testid="content-journal-campaign-filter"
              className="h-9 px-3 pr-7 text-[12.5px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)] rounded-full focus:outline-none focus:border-[hsl(var(--accent-gold))]"
            >
              <option value="">All campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => setFeaturedOnly((v) => !v)}
            data-testid="content-journal-featured-toggle"
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12.5px] border transition-colors ${
              featuredOnly
                ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/30"
                : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[rgba(0,0,0,0.06)] hover:text-[hsl(var(--foreground))]"
            }`}
          >
            <Star className="size-3" strokeWidth={1.5} />
            Featured
          </button>
          <PortalButton
            variant="primary"
            size="md"
            icon={<Plus className="size-3.5" strokeWidth={1.5} />}
            onClick={() => navigate("/advisor/content/journal/new")}
            data-testid="content-journal-new-entry"
          >
            New entry
          </PortalButton>
        </div>
      </div>

      <div>
        <EditorialRule
          label={filtered ? `${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}` : "loading"}
        />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<BookOpen className="size-6" strokeWidth={1.25} />} />
          ) : !filtered ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty
              title={query ? `No entries match "${query}"` : "No entries yet"}
              hint={
                query
                  ? "Try clearing search or switching filter."
                  : filter === "all"
                  ? "Begin a new piece — drafts are saved automatically."
                  : "Nothing matches this view."
              }
              icon={<BookOpen className="size-6" strokeWidth={1.25} />}
              action={
                filter === "all" && !query ? (
                  <PortalButton
                    size="sm"
                    variant="ghost"
                    icon={<Plus className="size-3.5" />}
                    onClick={() => navigate("/advisor/content/journal/new")}
                  >
                    Begin a new entry
                  </PortalButton>
                ) : null
              }
            />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {filtered.map((e) => (
                  <li key={e.id}>
                    <div
                      className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_160px_120px_auto] items-center gap-3 sm:gap-6 px-5 sm:px-6 py-4"
                      data-testid={`content-journal-row-${e.id}`}
                    >
                      <div className="min-w-0">
                        <button
                          onClick={() => navigate(`/advisor/content/journal/${e.id}`)}
                          className="text-left font-serif text-lg text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent-gold))] transition-colors truncate inline-flex items-center gap-1.5 w-full"
                          data-testid={`content-journal-edit-${e.id}`}
                        >
                          {e.featured && (
                            <Star
                              className="size-3.5 shrink-0 text-[hsl(var(--accent-gold))] fill-[hsl(var(--accent-gold))]"
                              strokeWidth={1.5}
                              data-testid={`content-journal-featured-${e.id}`}
                            />
                          )}
                          <span className="truncate">{e.title || "Untitled"}</span>
                        </button>
                        {e.subtitle && (
                          <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate">
                            {e.subtitle}
                          </p>
                        )}
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                          {[e.category, e.destination_tag].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <div className="hidden sm:block font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                        {e.status === "scheduled" && e.scheduled_publish_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-[hsl(var(--accent-gold))]"
                            data-testid={`content-journal-sched-${e.id}`}
                          >
                            Scheduled · {fmt.formatDate(e.scheduled_publish_at)}
                          </span>
                        ) : e.published_at ? (
                          fmt.formatDate(e.published_at)
                        ) : e.updated_at ? (
                          `Edited ${fmt.formatDate(e.updated_at)}`
                        ) : (
                          "—"
                        )}
                      </div>
                      <div className="hidden sm:block">
                        <StatusTag
                          tone={
                            e.status === "published"
                              ? "sage"
                              : e.status === "scheduled"
                              ? "gold"
                              : "neutral"
                          }
                        >
                          {e.status || "draft"}
                        </StatusTag>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setRepurposeEntry(e)}
                          title="Repurpose for social"
                          data-testid={`content-journal-repurpose-${e.id}`}
                          className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                        >
                          <Share2 className="size-3.5" strokeWidth={1.5} />
                        </button>
                        {e.status === "published" && e.slug && (
                          <a
                            href={`/blog/${e.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            title="View on public site"
                            data-testid={`content-journal-view-${e.id}`}
                            className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                          >
                            <ExternalLink className="size-3.5" strokeWidth={1.5} />
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/advisor/content/journal/${e.id}`)}
                          title="Edit"
                          className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
                        >
                          <Edit3 className="size-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleDelete(e)}
                          title="Delete"
                          data-testid={`content-journal-delete-${e.id}`}
                          className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
                        >
                          <Trash2 className="size-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </div>

      <RepurposeModal
        open={!!repurposeEntry}
        entry={repurposeEntry}
        onClose={() => setRepurposeEntry(null)}
        onSaved={(count) => {
          setRepurposeToast(`${count} social draft${count === 1 ? "" : "s"} saved.`);
          setTimeout(() => setRepurposeToast(null), 3500);
        }}
      />
      {repurposeToast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] shadow-2xl font-mono text-[11px] uppercase tracking-[0.18em]"
          data-testid="content-journal-repurpose-toast"
        >
          {repurposeToast}
        </div>
      )}
    </div>
  );
}
